import { useEffect, useState } from "react";
import axios from "axios";
import { X, Download, Copy, Check, ExternalLink, Loader2, Youtube, Music } from "lucide-react";
import { mediaUrl } from "@/mediaUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CopyBtn({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (_e) { /* ignored */ }
      }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 text-xs hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors shrink-0"
      data-testid={testid}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

/** Phase-1 YouTube distribution: one modal with the download + a
 *  ready-to-paste title/description so uploading a story to YouTube
 *  (or Spotify for Creators) takes ~30 seconds by hand. */
export default function YouTubeKitModal({ video, onClose }) {
  const [kit, setKit] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("youtube");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/videos/${video.id}/youtube-kit`);
        if (!cancelled) setKit(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [video.id]);

  const description = kit
    ? (tab === "youtube" ? kit.description : kit.spotify_description)
    : "";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="youtube-kit-backdrop"
    >
      <div
        className="w-full max-w-xl bg-[color:var(--panel)] border border-white/10 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="youtube-kit-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[color:var(--panel)] z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF0000]/15 border border-[#FF0000]/30 flex items-center justify-center">
              <Youtube className="w-5 h-5 text-[#ff5c5c]" />
            </div>
            <div>
              <div className="text-sm font-semibold">Upload kit</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                YouTube · Spotify — ready in 30 seconds
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center" data-testid="youtube-kit-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-200" data-testid="youtube-kit-error">{error}</div>
          )}
          {!kit && !error && (
            <div className="flex items-center gap-2 text-sm text-[color:var(--muted)]">
              <Loader2 className="w-4 h-4 animate-spin" /> Preparing your kit…
            </div>
          )}
          {kit && (
            <>
              {/* Step 1 — files */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-2">
                  Step 1 · Download the files
                </div>
                <div className="flex flex-wrap gap-2">
                  {kit.download_url && (
                    <a
                      href={mediaUrl(kit.download_url)}
                      download={kit.download_filename}
                      className="btn-primary text-sm inline-flex items-center gap-2"
                      data-testid="youtube-kit-download-video"
                    >
                      <Download className="w-4 h-4" /> Video MP4
                    </a>
                  )}
                  {kit.thumbnail_url && (
                    <a
                      href={mediaUrl(kit.thumbnail_url)}
                      download={kit.thumbnail_filename}
                      className="btn-ghost text-sm inline-flex items-center gap-2"
                      data-testid="youtube-kit-download-thumb"
                    >
                      <Download className="w-4 h-4" /> Thumbnail
                    </a>
                  )}
                </div>
              </div>

              {/* Step 2 — title */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-2">
                  Step 2 · Copy the title
                </div>
                <div className="flex items-center gap-2">
                  <div className="input text-sm flex-1 truncate py-2" data-testid="youtube-kit-title">{kit.title}</div>
                  <CopyBtn text={kit.title} testid="youtube-kit-copy-title" />
                </div>
              </div>

              {/* Step 3 — description with platform tabs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                    Step 3 · Copy the description
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setTab("youtube")}
                      className={`px-2.5 py-1 rounded-full text-[11px] inline-flex items-center gap-1 border transition-colors ${tab === "youtube" ? "border-[color:var(--accent)] text-[color:var(--accent)]" : "border-white/10 text-[color:var(--muted)]"}`}
                      data-testid="youtube-kit-tab-youtube"
                    >
                      <Youtube className="w-3 h-3" /> YouTube
                    </button>
                    <button
                      onClick={() => setTab("spotify")}
                      className={`px-2.5 py-1 rounded-full text-[11px] inline-flex items-center gap-1 border transition-colors ${tab === "spotify" ? "border-[#1DB954] text-[#1DB954]" : "border-white/10 text-[color:var(--muted)]"}`}
                      data-testid="youtube-kit-tab-spotify"
                    >
                      <Music className="w-3 h-3" /> Spotify
                    </button>
                  </div>
                </div>
                <textarea
                  readOnly
                  value={description}
                  rows={8}
                  className="w-full rounded-lg border border-white/10 bg-black/40 text-white/90 text-xs font-normal leading-relaxed p-3 focus:outline-none focus:border-[color:var(--accent)]"
                  data-testid="youtube-kit-description"
                />
                <div className="mt-2 flex items-center justify-between gap-2">
                  <CopyBtn text={description} testid="youtube-kit-copy-description" />
                  {tab === "youtube" && (
                    <CopyBtn text={(kit.tags || []).join(", ")} testid="youtube-kit-copy-tags" />
                  )}
                </div>
                {tab === "youtube" && (
                  <div className="text-[10px] text-[color:var(--muted)] mt-1">
                    Second Copy button = tags ({(kit.tags || []).join(", ")})
                  </div>
                )}
              </div>

              {/* Step 4 — go upload */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-2">
                  Step 4 · Upload
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="https://studio.youtube.com/channel/UC/videos/upload"
                    target="_blank" rel="noopener noreferrer"
                    className="btn-ghost text-sm inline-flex items-center gap-2"
                    data-testid="youtube-kit-open-youtube"
                  >
                    <Youtube className="w-4 h-4" /> Open YouTube Studio <ExternalLink className="w-3 h-3" />
                  </a>
                  <a
                    href="https://creators.spotify.com/"
                    target="_blank" rel="noopener noreferrer"
                    className="btn-ghost text-sm inline-flex items-center gap-2"
                    data-testid="youtube-kit-open-spotify"
                  >
                    <Music className="w-4 h-4" /> Open Spotify for Creators <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="text-[11px] text-[color:var(--muted)] mt-2 leading-relaxed">
                  YouTube: drag the MP4 in, paste title + description + tags, set the thumbnail, publish.
                  Spotify: open your episode → Add video → upload the same MP4.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
